import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

// Rendimiento thresholds: >50% Excelente, 45-50% Bueno, 40-45% Revisar, <40% Bajo
const getRendimientoInfo = (rendimiento) => {
  if (rendimiento == null) return null;
  const r = parseFloat(rendimiento);
  if (r > 50) return { label: 'Excelente', emoji: '🟢', bg: 'rgba(58,127,107,0.18)', border: 'rgba(58,127,107,0.45)', color: '#3A7F6B' };
  if (r >= 45) return { label: 'Bueno', emoji: '🟢', bg: 'rgba(58,127,107,0.12)', border: 'rgba(58,127,107,0.30)', color: '#5aab8e' };
  if (r >= 40) return { label: 'Revisar', emoji: '🟡', bg: 'rgba(201,162,77,0.18)', border: 'rgba(201,162,77,0.45)', color: '#C9A24D' };
  return { label: 'Bajo Rendimiento', emoji: '🔴', bg: 'rgba(224,82,82,0.18)', border: 'rgba(224,82,82,0.45)', color: '#E05252' };
};

const getMermaInfo = (merma, pesoInicial) => {
  if (merma == null || pesoInicial == null || parseFloat(pesoInicial) === 0) return null;
  const pct = (parseFloat(merma) / parseFloat(pesoInicial)) * 100;
  if (pct < 10) return { bg: 'rgba(58,127,107,0.18)', border: 'rgba(58,127,107,0.40)', color: '#3A7F6B' };
  if (pct < 20) return { bg: 'rgba(201,162,77,0.18)', border: 'rgba(201,162,77,0.40)', color: '#C9A24D' };
  return { bg: 'rgba(224,82,82,0.18)', border: 'rgba(224,82,82,0.40)', color: '#E05252' };
};

const ESTADO_PLAN = {
  PENDIENTE:   { icon: '○', cls: 'text-white/40' },
  RECIBIDO:    { icon: '📦', cls: 'text-blue-400' },
  EN_PROCESO:  { icon: '▶', cls: 'text-yellow-400' },
  COMPLETADO:  { icon: '✓', cls: 'text-green-400' },
  INCIDENCIA:  { icon: '!', cls: 'text-red-400' },
};

const LoncheadosTable = ({ loncheados, clientes, empleados, productos, onEdit, onDelete, onVerDetalle }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedLoncheados = [...(loncheados || [])]?.sort((a, b) => {
    const aValue = a?.[sortConfig?.key];
    const bValue = b?.[sortConfig?.key];
    if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
    return 0;
  });

  const getClienteName = (loncheado) =>
    loncheado?.clienteNombre ||
    clientes?.find(c => c?.id === loncheado?.clienteId)?.nombre ||
    'N/A';
  const getEmpleadoName = (loncheado) =>
    loncheado?.empleadoNombre ||
    empleados?.find(e => e?.id === loncheado?.empleadoId)?.nombre ||
    'N/A';
  const getProductoName = (loncheado) =>
    loncheado?.productoNombre ||
    productos?.find(p => p?.id === loncheado?.productoId)?.nombre ||
    'N/A';

  const SortIcon = ({ col }) => (
    <span className="ml-1 opacity-60">
      {sortConfig?.key === col ? (sortConfig?.direction === 'asc' ? '↑' : '↓') : '↕'}
    </span>
  );

  const thClass = "px-4 py-4 text-left text-[14px] font-bold font-oranienbaum text-[#D6EF4E] whitespace-nowrap select-none";
  const thSortClass = thClass + " cursor-pointer hover:text-white transition-colors";
  const tdClass = "px-4 py-4 text-[14px] font-oranienbaum text-white/85 whitespace-nowrap";

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[1300px]">
        <thead>
          <tr style={{ background: 'rgba(214,239,78,0.06)', borderBottom: '1px solid rgba(214,239,78,0.15)' }}>
            <th className={thSortClass} onClick={() => handleSort('createdAt')}>
              Fecha <SortIcon col="createdAt" />
            </th>
            <th className={thClass}>Cliente</th>
            <th className={thClass}>Empleado</th>
            <th className={thClass}>Producto</th>
            <th className={thSortClass} onClick={() => handleSort('pesoInicial')}>
              P. Inicial (kg) <SortIcon col="pesoInicial" />
            </th>
            <th className={thSortClass} onClick={() => handleSort('pesoAprovechado')}>
              P. Aprovechado (kg) <SortIcon col="pesoAprovechado" />
            </th>
            <th className={thSortClass} onClick={() => handleSort('merma')}>
              Merma (kg) <SortIcon col="merma" />
            </th>
            <th className={thSortClass} onClick={() => handleSort('rendimiento')}>
              Rendimiento % <SortIcon col="rendimiento" />
            </th>
            <th className={thClass}>Estado Rendimiento</th>
            <th className={thClass}>Plan de Trabajo</th>
            <th className="px-4 py-4 text-right text-[14px] font-bold font-oranienbaum text-[#D6EF4E]">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {sortedLoncheados?.length === 0 ? (
            <tr>
              <td colSpan="11" className="px-5 py-12 text-center text-white/40 font-oranienbaum text-base">
                No hay registros para mostrar
              </td>
            </tr>
          ) : (
            sortedLoncheados?.map((loncheado, index) => {
              const rendInfo = getRendimientoInfo(loncheado?.rendimiento);
              const mermaInfo = getMermaInfo(loncheado?.merma, loncheado?.pesoInicial);
              const planTrabajo = loncheado?.planTrabajo;
              const estadoPlan = planTrabajo ? (ESTADO_PLAN?.[planTrabajo?.estado] || ESTADO_PLAN?.PENDIENTE) : null;

              return (
                <tr
                  key={loncheado?.id}
                  style={{
                    background: index % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.025)',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                    transition: 'background 0.15s'
                  }}
                  className="hover:bg-white/5"
                >
                  <td className={tdClass}>
                    {new Date(loncheado?.createdAt)?.toLocaleString('es-ES', {
                      year: 'numeric', month: '2-digit', day: '2-digit',
                      hour: '2-digit', minute: '2-digit'
                    })}
                  </td>
                  <td className={tdClass}>{getClienteName(loncheado)}</td>
                  <td className={tdClass}>{getEmpleadoName(loncheado)}</td>
                  <td className={tdClass + " max-w-[140px] truncate text-xs"}>{getProductoName(loncheado)}</td>
                  <td className={tdClass}>
                    {loncheado?.pesoInicial != null ? parseFloat(loncheado?.pesoInicial)?.toFixed(2) : '-'}
                  </td>
                  <td className={tdClass}>
                    {loncheado?.pesoAprovechado != null ? parseFloat(loncheado?.pesoAprovechado)?.toFixed(2) : '-'}
                  </td>

                  {/* Merma with color badge */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    {loncheado?.merma != null ? (
                      <span
                        className="inline-flex items-center px-2.5 py-1 rounded-lg text-[13px] font-bold font-oranienbaum"
                        style={{
                          background: mermaInfo?.bg || 'rgba(255,255,255,0.06)',
                          border: `1px solid ${mermaInfo?.border || 'rgba(255,255,255,0.12)'}`,
                          color: mermaInfo?.color || 'rgba(255,255,255,0.7)'
                        }}
                      >
                        {parseFloat(loncheado?.merma)?.toFixed(2)} kg
                      </span>
                    ) : (
                      <span className="text-white/30 font-oranienbaum text-sm">-</span>
                    )}
                  </td>

                  {/* Rendimiento % badge */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    {rendInfo ? (
                      <span
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-[13px] font-bold font-oranienbaum"
                        style={{
                          background: rendInfo?.bg,
                          border: `1px solid ${rendInfo?.border}`,
                          color: rendInfo?.color
                        }}
                      >
                        {rendInfo?.emoji} {parseFloat(loncheado?.rendimiento)?.toFixed(1)}%
                      </span>
                    ) : (
                      <span className="text-white/30 font-oranienbaum text-sm">-</span>
                    )}
                  </td>

                  {/* Estado Rendimiento badge */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    {rendInfo ? (
                      <span
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-[12px] font-bold font-oranienbaum"
                        style={{
                          background: rendInfo?.bg,
                          border: `1px solid ${rendInfo?.border}`,
                          color: rendInfo?.color
                        }}
                      >
                        {rendInfo?.emoji} {rendInfo?.label}
                      </span>
                    ) : (
                      <span className="text-white/30 font-oranienbaum text-sm">-</span>
                    )}
                  </td>

                  {/* Plan de Trabajo context */}
                  <td className="px-4 py-4 whitespace-nowrap">
                    {planTrabajo ? (
                      <button
                        onClick={() => window.location.href = '/plan-trabajo'}
                        className="flex flex-col items-start gap-0.5 hover:opacity-80 transition-opacity text-left"
                        title="Abrir en Plan de Trabajo"
                      >
                        <div className="flex items-center gap-1.5">
                          <span className={`text-xs ${estadoPlan?.cls}`}>{estadoPlan?.icon}</span>
                          <span className="text-[#D6EF4E]/70 text-xs font-oranienbaum font-bold">
                            {planTrabajo?.cliente?.nombre || '—'}
                          </span>
                        </div>
                        <span className="text-white/30 text-[10px] font-oranienbaum">
                          {planTrabajo?.fecha ? new Date(planTrabajo?.fecha + 'T12:00:00')?.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }) : ''}
                          {planTrabajo?.cantidad_piezas ? ` · ${planTrabajo?.cantidad_piezas} piezas` : ''}
                        </span>
                      </button>
                    ) : (
                      <span className="text-white/20 font-oranienbaum text-xs">Manual</span>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-4 text-right">
                    <div className="flex gap-1.5 justify-end">
                      <button
                        onClick={() => onVerDetalle?.(loncheado)}
                        title="Ver detalle"
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-white/50 hover:text-[#D6EF4E] hover:bg-[#D6EF4E]/10 transition-all"
                      >
                        <Icon name="Eye" size={15} />
                      </button>
                      <button
                        onClick={() => onEdit?.(loncheado)}
                        title="Editar"
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-white/50 hover:text-[#3A7F6B] hover:bg-[#3A7F6B]/10 transition-all"
                      >
                        <Icon name="Edit" size={15} />
                      </button>
                      <button
                        onClick={() => onDelete?.(loncheado?.id)}
                        title="Eliminar"
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-white/50 hover:text-[#E05252] hover:bg-[#E05252]/10 transition-all"
                      >
                        <Icon name="Trash2" size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default LoncheadosTable;